import { HTTPMethod } from "@fusionauth/typescript-client";
import DeleteIconOutlined from "@mui/icons-material/DeleteOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  type SnackbarCloseReason,
  TextField,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid-premium";
import React, { useContext, useEffect, useState } from "react";
import CustomDialog from "../../../components/CustomDialog.tsx";
import BaseTable from "../../../components/datagrid/BaseTable.tsx";
import { TableNoRowsCustomization } from "../../../components/datagrid/TableNoRowsCustomization.tsx";
import {
  ProjectContext,
  ProjectDispatchContext,
} from "../../../contexts/ProjectContext";
import { useFetch } from "../../../services/hooks/useFetch";
import theme from "../../../styles/theme.ts";
import { apiPrefix } from "../../../utils/main";

interface ReasonRequest {
  reason: string;
}

const CustomNoUserRowsOverlay = () => {
  return (
    <TableNoRowsCustomization
      icon={
        <LockOutlinedIcon
          style={{
            color: "#0D3E56",
            width: "32px",
            height: "32px",
            marginLeft: "18px",
          }}
        />
      }
      text={"Only ADMINS can access this project."}
      subtext={"Once access has been provided the users will be shown here."}
      iconBackground={48}
    />
  );
};

export default function ProjectPermissionsPage() {
  const { id, isLocked } = useContext(ProjectContext) || { isLocked: false };
  const dispatch = useContext(ProjectDispatchContext);

  const [locked, setLocked] = useState<boolean>(isLocked || false);
  const [updateDetails, setUpdateDetails] = useState<string>("");
  const [displayErrors, setDisplayErrors] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [confirmationRow, setConfirmationRow] = useState<
    UserProject | undefined
  >(undefined);

  const { update: updateProjectLock } = useFetch<void, ReasonRequest>();
  const {
    data: projectUsers,
    fetch: fetchProjectUsers,
    isLoading: projectUsersLoading,
  } = useFetch<UserProject[], Partial<UserProject>>();
  const { delete: deleteUserProjects } = useFetch<
    undefined,
    Partial<UserProject>
  >();

  const userTableFields: GridColDef[] = [
    {
      field: "user.email",
      valueGetter: (_value, row) => {
        return row.user?.email;
      },
      headerName: "User Email",
      minWidth: 250,
      flex: 1,
    },
    {
      field: "user.fullName",
      valueGetter: (_value, row) => {
        return row.user?.fullName;
      },
      headerName: "User Name",
      minWidth: 250,
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 150,
      align: "center",
      getActions: ({ row }) => {
        return row.id
          ? [
              <GridActionsCellItem
                key={row.id}
                icon={<DeleteIconOutlined fontSize="small" />}
                title="Revoke Access"
                label="Revoke Access"
                className="textPrimary"
                onClick={() => {
                  setShowConfirmation(true);
                  setConfirmationRow(row);
                }}
                color="inherit"
              />,
            ]
          : [];
      },
    },
  ];

  const submitLockUnlock = function () {
    if (updateDetails === "") {
      setDisplayErrors(true);
      return;
    }

    void updateProjectLock({
      path: `${apiPrefix}/projects/${id}/${locked ? "lock" : "unlock"}`,
      body: {
        reason: updateDetails,
      },
      method: HTTPMethod.PATCH,
      postResponseAction: () => {
        if (dispatch !== null) {
          dispatch({
            type: "update",
            project: {
              isLocked: locked,
            },
          });
        }
      },
    });

    setUpdateDetails("");
    setDisplayErrors(false);
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    // this timeout prevents the weird flash of the dynamic modal contents
    // disappearing before the dialog actually transitions out
    setTimeout(() => {
      setConfirmationRow(undefined);
    }, theme.transitions.duration.leavingScreen);
  };

  const handleRevokeAccess = (userProject: UserProject) => {
    void deleteUserProjects({
      path: `${apiPrefix}/user-project/${userProject.id}`,
      postResponseAction: () => {
        // refetch user projects
        void fetchProjectUsers(`${apiPrefix}/user-project/project/${id}`);
        setShowSnackbar(true);
        setShowConfirmation(false);
      },
    });
  };

  const handleSnackbarClose = (
    _event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setShowSnackbar(false);
    setConfirmationRow(undefined);
  };

  useEffect(() => {
    if (id && !projectUsersLoading && !projectUsers) {
      void fetchProjectUsers(`${apiPrefix}/user-project/project/${id}`);
    }
  }, [id, fetchProjectUsers, projectUsers, projectUsersLoading]);

  return (
    <Grid container direction={"column"} className="company-settings-item">
      <div className="company-settings-item">
        <Grid>
          <Typography variant="h4">Project Permissions</Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">
            Edit permissions for accessing and editing this project.
          </Typography>
        </Grid>
        <br />
        <Grid>
          <Typography variant="body2">* Required fields</Typography>
        </Grid>
        <br />
        <Grid>
          <InputLabel shrink htmlFor="select-project-locked" required>
            Project Permissions
          </InputLabel>
          <Select
            required
            variant="outlined"
            inputProps={{ id: "select-project-locked" }}
            fullWidth
            value={locked}
            onChange={(event) => {
              setLocked(event.target.value !== "false");
            }}
          >
            <MenuItem value={"true"}>Locked</MenuItem>
            <MenuItem value={"false"}>Unlocked</MenuItem>
          </Select>
        </Grid>
        {locked !== isLocked && (
          <>
            <br />
            <Grid>
              <TextField
                required
                label="Note/Reason for Updating the Project Permissions"
                multiline
                minRows={2}
                fullWidth
                value={updateDetails}
                error={displayErrors && updateDetails === ""}
                helperText={
                  displayErrors && updateDetails === ""
                    ? "This field is required"
                    : ""
                }
                onChange={(event) => {
                  setUpdateDetails(event.target.value);
                }}
              ></TextField>
            </Grid>
            <br />
            <Grid container justifyContent={"right"}>
              <Button
                variant="outlined"
                onClick={() => {
                  setUpdateDetails("");
                  setLocked(!!isLocked);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="update-button"
                sx={{ marginLeft: "5px" }}
                disabled={!id && locked !== undefined}
                onClick={() => {
                  submitLockUnlock();
                }}
              >
                Update
              </Button>
            </Grid>
          </>
        )}
        <br />
        <Grid>
          <Typography variant="h6">Users with access</Typography>
          <BaseTable<UserProject>
            tableHeight={300}
            disableColumnFilter
            slots={{
              noRowsOverlay: CustomNoUserRowsOverlay,
            }}
            slotProps={{
              toolbar: {
                showViewMenu: false,
                showQuickFilter: true,
                showFilter: false,
                showExportCsv: true,
              },
            }}
            columns={userTableFields}
            loading={projectUsersLoading}
            rows={projectUsers || []}
            sx={{
              "& .MuiDataGrid-cell": {
                alignContent: "center",
              },
            }}
            tableId="project-users-table"
          />
        </Grid>
      </div>
      <CustomDialog
        open={showConfirmation}
        handleClose={handleCloseConfirmation}
        hasConfirmation
        handleConfirm={() => {
          if (confirmationRow) {
            handleRevokeAccess(confirmationRow);
          }
        }}
        confirmationText={"Revoke Access"}
      >
        <div
          style={{
            width: "350px",
          }}
        >
          <Typography variant="body1">
            Revoke access to the {confirmationRow?.project.name} project for{" "}
            {confirmationRow?.user?.fullName}?
          </Typography>
        </div>
      </CustomDialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Access revoked to {confirmationRow?.project.name} for{" "}
          {confirmationRow?.user?.fullName}.
        </Alert>
      </Snackbar>
    </Grid>
  );
}
